import { inject, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useTranslation } from 'i18next-vue'
import { useWatchlistStore } from '@/stores'
import { $axios } from '@/boot/initApi'

/**
 * The three ways other pages put things into a watchlist, in one place so the Search and Brand
 * pages stop carrying their own copies:
 *
 * - `saveBrands`: brands picked on the search page. Each brand goes in **with its current
 *   presentations** (the default; the user can trim them on the watchlist afterwards). Every
 *   presentation is fetched *before* anything is written, so a failed lookup never leaves a
 *   half-added selection behind.
 * - `savePresentations`: presentations picked on a brand page; the brand goes in with them.
 * - `saveSearch`: the current search filters, saved as a *new* watchlist's criteria (a search
 *   never replaces an existing watchlist's filters).
 *
 * Additions use the additive endpoint, so nothing already tracked is read back and re-sent (no
 * lost updates), and each success toast offers to open the watchlist.
 */
export const useSaveToWatchlist = () => {
  const $toast = inject('$toast')
  const $router = useRouter()
  const { t } = useTranslation('watchlist')
  const { addItemsToWatchlist, updateWatchlist } = useWatchlistStore()
  const { mutateAsync: addItems, isPending: isAddingItems } = addItemsToWatchlist()
  const { mutateAsync: patchWatchlist, isPending: isSavingSearch } = updateWatchlist()

  const isSaving = computed(() => isAddingItems.value || isSavingSearch.value)

  const openAction = watchlist => ({
    label: t('Toast.saved.open'),
    dismiss: true,
    onClick: () => $router.push({ name: 'Watchlist', params: { watchlistId: watchlist.id } })
  })

  const presentationIdsOf = async brandIds => {
    const responses = await Promise.all(brandIds.map(brandId => $axios.get(`/api/brands/${brandId}`)))
    return responses.flatMap(({ data }) => (data?.presentations ?? []).map(presentation => presentation.presentationId))
  }

  const toastItemsSaved = ({ watchlist, created, brandCount, presentationCount }) => $toast({
    type: 'success',
    title: t(`Toast.${created ? 'savedItemsNew' : 'savedItems'}.title`),
    caption: t(`Toast.${created ? 'savedItemsNew' : 'savedItems'}.caption`, {
      count: presentationCount,
      name: watchlist.title,
      brandCount,
      presentationCount
    }),
    actions: [openAction(watchlist)]
  })

  const saveBrands = async ({ watchlist, brandIds, created = false }) => {
    const presentationIds = await presentationIdsOf(brandIds)
    await addItems({ id: watchlist.id, brandIds, presentationIds })
    toastItemsSaved({ watchlist, created, brandCount: brandIds.length, presentationCount: presentationIds.length })
    return { brandCount: brandIds.length, presentationCount: presentationIds.length }
  }

  const savePresentations = async ({ watchlist, brandId, presentationIds, created = false }) => {
    await addItems({ id: watchlist.id, brandIds: [brandId], presentationIds })
    toastItemsSaved({ watchlist, created, brandCount: 1, presentationCount: presentationIds.length })
    return { brandCount: 1, presentationCount: presentationIds.length }
  }

  const saveSearch = async ({ watchlist, filters }) => {
    await patchWatchlist({ id: watchlist.id, filters })
    $toast({
      type: 'success',
      title: t('Toast.savedSearch.title'),
      caption: t('Toast.savedSearch.caption', { name: watchlist.title }),
      actions: [openAction(watchlist)]
    })
  }

  return { saveBrands, savePresentations, saveSearch, isSaving }
}
